import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  motion,
  useScroll,
  useTransform,
  useSpring,
  AnimatePresence,
} from "framer-motion";
import {
  FaChartLine,
  FaHistory,
  FaBullseye,
  FaCog,
  FaArrowDown,
  FaCheck,
  FaStar,
  FaQuoteLeft,
  FaCrosshairs,
} from "react-icons/fa";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import WireDartboard from "../components/WireDartboard";

const LandingPage = () => {
  const { t } = useTranslation();
  const { isLoggedIn } = useSelector((state) => state.user);
  const [activeSection, setActiveSection] = useState("hero");
  const [showScrollIndicator, setShowScrollIndicator] = useState(true);
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  // Scroll progress for progress bar
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001,
  });

  // Parallax effect for hero section
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 500], [0, -150]);

  // Mouse position effect for dynamic background
  useEffect(() => {
    const handleMouseMove = (e) => {
      // Get the relative position within the viewport
      const x = (e.clientX / window.innerWidth) * 100;
      const y = (e.clientY / window.innerHeight) * 100;
      setMousePosition({ x, y });

      // Update the CSS variable for the glow position
      document.documentElement.style.setProperty("--mouse-x", `${x}%`);
      document.documentElement.style.setProperty("--mouse-y", `${y}%`);
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        "hero",
        "stats",
        "features",
        "comparison",
        "testimonials",
        "cta",
      ];
      const currentSection = sections.find((section) => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 300 && rect.bottom >= 300;
        }
        return false;
      });

      if (currentSection) {
        setActiveSection(currentSection);
      }

      // Hide scroll indicator when user starts scrolling
      if (window.scrollY > 100) {
        setShowScrollIndicator(false);
      } else {
        setShowScrollIndicator(true);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Auto-rotate testimonials
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const features = [
    {
      icon: <FaBullseye className="w-8 h-8 text-test" />,
      title: t("home.features.fastResults.title"),
      description: t("home.features.fastResults.description"),
      details: [],
    },
    {
      icon: <FaChartLine className="w-8 h-8 text-test" />,
      title: t("home.features.realInsights.title"),
      description: t("home.features.realInsights.description"),
      details: [],
    },
    {
      icon: <FaHistory className="w-8 h-8 text-test" />,
      title: t("home.features.smartTraining.title"),
      description: t("home.features.smartTraining.description"),
      details: [],
    },
    {
      icon: <FaCog className="w-8 h-8 text-test" />,
      title: t("home.features.motivation.title"),
      description: t("home.features.motivation.description"),
      details: [],
    },
  ];

  const stats = [
    { number: "100+", label: t("home.stats.activePlayers") },
    { number: "1K+", label: t("home.stats.gamesTracked") },
    { number: "98%", label: t("home.stats.satisfactionRate") },
    { number: "24/7", label: t("home.stats.support") },
  ];

  const testimonials = [
    {
      quote: t("home.testimonials.player1.quote"),
      author: t("home.testimonials.player1.author"),
      title: t("home.testimonials.player1.title"),
      rating: 5,
    },
    {
      quote: t("home.testimonials.player2.quote"),
      author: t("home.testimonials.player2.author"),
      title: t("home.testimonials.player2.title"),
      rating: 5,
    },
    {
      quote: t("home.testimonials.player3.quote"),
      author: t("home.testimonials.player3.author"),
      title: t("home.testimonials.player3.title"),
      rating: 5,
    },
    {
      quote: t("home.testimonials.player4.quote"),
      author: t("home.testimonials.player4.author"),
      title: t("home.testimonials.player4.title"),
      rating: 5,
    },
    {
      quote: t("home.testimonials.player5.quote"),
      author: t("home.testimonials.player5.author"),
      title: t("home.testimonials.player5.title"),
      rating: 5,
    },
  ];

  const comparisonFeatures = [
    {
      feature: t("home.comparison.features.sessionTracking"),
      free: true,
      pro: true,
    },
    {
      feature: t("home.comparison.features.basicStats"),
      free: true,
      pro: false,
    },
    {
      feature: t("home.comparison.features.basicStrategies"),
      free: true,
      pro: true,
    },
    {
      feature: t("home.comparison.features.advancedStats"),
      free: false,
      pro: true,
    },
    {
      feature: t("home.comparison.features.advancedStrategies"),
      free: false,
      pro: true,
    },
    {
      feature: t("home.comparison.features.personalizedTraining"),
      free: false,
      pro: true,
    },
  ];

  const faqs = [
    {
      question: t("home.faq.whatIs.question"),
      answer: t("home.faq.whatIs.answer"),
    },
    {
      question: t("home.faq.howItWorks.question"),
      answer: t("home.faq.howItWorks.answer"),
    },
    {
      question: t("home.faq.betterThanTraditional.question"),
      answer: t("home.faq.betterThanTraditional.answer"),
    },
    {
      question: t("home.faq.insights.question"),
      answer: t("home.faq.insights.answer"),
    },
    {
      question: t("home.faq.personalizedTraining.question"),
      answer: t("home.faq.personalizedTraining.answer"),
    },
    {
      question: t("home.faq.forWhom.question"),
      answer: t("home.faq.forWhom.answer"),
    },
    {
      question: t("home.faq.compareConditions.question"),
      answer: t("home.faq.compareConditions.answer"),
    },
    {
      question: t("home.faq.dataSafety.question"),
      answer: t("home.faq.dataSafety.answer"),
    },
    {
      question: t("home.faq.equipment.question"),
      answer: t("home.faq.equipment.answer"),
    },
  ];

  // Dynamic background pattern that changes with mouse position
  const DynamicBackground = ({ className = "" }) => (
    <>
      <div
        className={`absolute inset-0 transition-opacity duration-300 ${className}`}
      >
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")",
            backgroundSize: "30px 30px",
          }}
        />
      </div>
      <div
        className={`absolute inset-0 transition-opacity duration-300 ${className}`}
      >
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ff8f00' fill-opacity='0.35'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")",
            backgroundSize: "30px 30px",
            mask: "radial-gradient(circle 300px at var(--mouse-x) var(--mouse-y), black 10%, transparent 70%)",
          }}
        />
      </div>
      <div
        className={`absolute inset-0 transition-opacity duration-300 pointer-events-none ${className}`}
      >
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(circle 200px at var(--mouse-x) var(--mouse-y), rgba(255, 143, 0, 0.15), transparent 70%)",
            mixBlendMode: "plus-lighter",
          }}
        />
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-primary w-full relative">
      <style>
        {`
          :root {
            --mouse-x: 50%;
            --mouse-y: 50%;
          }
          
          @keyframes glow-pulse {
            0% { opacity: 0.8; }
            50% { opacity: 1; }
            100% { opacity: 0.8; }
          }
          
          .glow-effect {
            animation: glow-pulse 2s ease-in-out infinite;
          }
        `}
      </style>
      {/* Progress bar */}
      <motion.div
        className="fixed top-0 left-0 right-0 h-1 bg-test z-50"
        style={{ scaleX, transformOrigin: "0%" }}
      />

      {/* Hero Section */}
      <section
        id="hero"
        className="relative min-h-screen flex items-center overflow-hidden"
      >
        <motion.div
          style={{ y }}
          className="absolute inset-0 pointer-events-none"
        >
          <div className="absolute inset-0 bg-gradient-to-b from-primary/50 to-primary" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(var(--color-test-rgb),0.1)_0%,transparent_100%)]" />
          <DynamicBackground />
        </motion.div>

        <div className="w-full max-w-7xl mx-auto px-4 md:px-6 relative grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="text-left relative z-10"
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              <div className="flex items-center gap-3 text-test mb-6">
                <FaBullseye className="w-8 h-8" />
                <span className="text-xl font-semibold">
                  {t("home.hero.welcome")}
                </span>
              </div>
              <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-test mb-6 leading-tight">
                {t("home.hero.title")}
              </h1>
              <div className="relative">
                <p className="text-xl md:text-2xl text-white/80 mb-8 leading-relaxed">
                  {t("home.hero.description")}
                </p>
              </div>
              <div className="flex flex-wrap gap-6">
                {!isLoggedIn && (
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Link
                      to="/login"
                      className="px-8 py-4 bg-test text-primary rounded-xl font-semibold hover:bg-test/90 transition-colors shadow-lg text-lg group inline-flex items-center"
                    >
                      {t("home.hero.getStarted")}
                      <span className="ml-2 inline-block transition-transform group-hover:translate-x-1">
                        →
                      </span>
                    </Link>
                  </motion.div>
                )}
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Link
                    to="/selection"
                    className="px-8 py-4 bg-secondary text-test rounded-xl font-semibold hover:bg-secondary/90 transition-colors shadow-lg text-lg inline-flex items-center"
                  >
                    {t("home.hero.startNow")}
                  </Link>
                </motion.div>
              </div>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            className="relative hidden lg:block"
          >
            <WireDartboard />
          </motion.div>
        </div>

        {showScrollIndicator && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.5, duration: 0.5 }}
            className="absolute bottom-28 left-1/2 transform -translate-x-1/2 text-white/60 flex flex-col items-center gap-2"
          >
            <span>{t("home.hero.scrollToExplore")}</span>
            <FaArrowDown className="animate-bounce" />
          </motion.div>
        )}
      </section>

      {/* Stats Section */}
      <section id="stats" className="py-20 px-4 md:px-6 bg-primary/50 relative">
        <div className="w-full">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <motion.div
                  initial={{ scale: 0 }}
                  whileInView={{ scale: 1 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="text-4xl md:text-5xl font-bold text-test mb-2"
                >
                  {stat.number}
                </motion.div>
                <p className="text-white/60 text-lg">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section with Extended Content */}
      <section
        id="features"
        className="py-32 px-4 md:px-6 bg-secondary relative"
      >
        <DynamicBackground className="opacity-30" />

        <div className="w-full">
          <motion.div className="text-center mb-20">
            <motion.h2
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="text-3xl md:text-5xl font-bold text-test mb-6"
            >
              {t("home.features.title")}
            </motion.h2>
            <p className="text-xl text-white/70 max-w-3xl mx-auto">
              {t("home.features.description")}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ scale: 1.05 }}
                className="bg-primary p-8 rounded-2xl shadow-lg border border-test/10 hover:border-test/30 transition-all duration-300 group"
              >
                <motion.div
                  transition={{ duration: 0.5 }}
                  className="mb-6 transform group-hover:scale-110 transition-transform"
                >
                  {feature.icon}
                </motion.div>
                <h3 className="text-2xl font-semibold text-test mb-4">
                  {feature.title}
                </h3>
                <p className="text-white/80 text-lg leading-relaxed mb-6">
                  {feature.description}
                </p>
                <ul className="space-y-3">
                  {feature.details.map((detail, idx) => (
                    <li
                      key={idx}
                      className="flex items-start gap-2 text-white/70"
                    >
                      <span className="text-test">•</span>
                      {detail}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-32 px-4 md:px-6 bg-primary/80 relative">
        <DynamicBackground className="opacity-30" />
        <div className="w-full">
          <motion.div className="text-center mb-20">
            <h2 className="text-3xl md:text-5xl font-bold text-test mb-6">
              {t("home.howItWorks.title")}
            </h2>
            <p className="text-xl text-white/70 max-w-3xl mx-auto">
              {t("home.howItWorks.description")}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {[
              {
                step: "1",
                title: t("home.howItWorks.step1.title"),
                description: t("home.howItWorks.step1.description"),
              },
              {
                step: "2",
                title: t("home.howItWorks.step2.title"),
                description: t("home.howItWorks.step2.description"),
              },
              {
                step: "3",
                title: t("home.howItWorks.step3.title"),
                description: t("home.howItWorks.step3.description"),
              },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 rounded-full bg-test text-primary text-2xl font-bold flex items-center justify-center mx-auto mb-6">
                  {item.step}
                </div>
                <h3 className="text-2xl font-semibold text-test mb-4">
                  {item.title}
                </h3>
                <p className="text-white/70 text-lg">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-32 px-4 md:px-6 bg-secondary relative">
        <DynamicBackground className="opacity-30" />
        <div className="w-full">
          <div className="max-w-5xl mx-auto">
            <motion.div className="text-center mb-20">
              <h2 className="text-3xl md:text-5xl font-bold text-test mb-6">
                {t("home.faq.title")}
              </h2>
              <p className="text-xl text-white/70">
                {t("home.faq.description")}
              </p>
            </motion.div>

            <div className="space-y-6">
              {faqs.map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-primary p-6 rounded-xl"
                >
                  <h3 className="text-xl font-semibold text-test mb-3">
                    {faq.question}
                  </h3>
                  <p className="text-white/70 leading-relaxed">{faq.answer}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section
        id="comparison"
        className="py-32 px-4 md:px-6 bg-primary/80 relative"
      >
        <DynamicBackground className="opacity-30" />
        <div className="w-full">
          <div className="max-w-5xl mx-auto">
            <motion.h2
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="text-3xl md:text-5xl font-bold text-test text-center mb-20"
            >
              {t("home.comparison.title")}
            </motion.h2>
            <div className="bg-secondary rounded-2xl shadow-xl overflow-hidden">
              <div className="grid grid-cols-3 text-center py-6 bg-primary">
                <div className="text-white/60">
                  {t("home.comparison.features.title")}
                </div>
                <div className="text-test font-semibold">
                  {t("home.comparison.free")}
                </div>
                <div className="text-test font-semibold">
                  {t("home.comparison.pro")}
                </div>
              </div>
              {comparisonFeatures.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="grid grid-cols-3 py-4 px-6 border-b border-primary/10 items-center"
                >
                  <div className="text-white/80">{item.feature}</div>
                  <div className="text-center">
                    {item.free ? (
                      <FaCheck className="w-5 h-5 text-green-500 mx-auto" />
                    ) : (
                      <span className="text-white/30">—</span>
                    )}
                  </div>
                  <div className="text-center">
                    <FaCheck className="w-5 h-5 text-test mx-auto" />
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section
        id="testimonials"
        className="py-32 px-4 md:px-6 bg-secondary relative overflow-hidden"
      >
        <DynamicBackground className="opacity-30" />
        <div className="w-full">
          <div className="max-w-6xl mx-auto">
            <motion.h2
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="text-3xl md:text-5xl font-bold text-test text-center mb-20"
            >
              {t("home.testimonials.title")}
            </motion.h2>
            <div className="relative">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeTestimonial}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.5 }}
                  className="text-center"
                >
                  <FaQuoteLeft className="w-12 h-12 text-test/20 mx-auto mb-8" />
                  <p className="text-2xl text-white/90 mb-8 italic max-w-3xl mx-auto">
                    "{testimonials[activeTestimonial].quote}"
                  </p>
                  <div className="flex justify-center items-center gap-2 mb-4">
                    {[...Array(testimonials[activeTestimonial].rating)].map(
                      (_, i) => (
                        <FaStar key={i} className="w-6 h-6 text-yellow-500" />
                      )
                    )}
                  </div>
                  <div className="text-test font-semibold text-xl">
                    {testimonials[activeTestimonial].author}
                  </div>
                  <div className="text-white/60">
                    {testimonials[activeTestimonial].title}
                  </div>
                </motion.div>
              </AnimatePresence>
              <div className="flex justify-center mt-8 gap-2">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveTestimonial(index)}
                    className={`w-2 h-2 rounded-full transition-all duration-300 ${
                      activeTestimonial === index
                        ? "w-8 bg-test"
                        : "bg-white/30"
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Enhanced CTA Section */}
      <section id="cta" className="py-32 px-4 md:px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary to-secondary opacity-50" />
        <DynamicBackground className="opacity-30" />
        <div className="w-full">
          <div className="max-w-5xl mx-auto text-center relative">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-4xl md:text-5xl font-bold text-test mb-8">
                {t("home.cta.title")}
              </h2>
              <p className="text-xl md:text-2xl text-white/80 mb-6 leading-relaxed">
                {t("home.cta.description")}
              </p>
              <p className="text-lg text-white/60 mb-12">
                {t("home.cta.subDescription")}
              </p>
              {!isLoggedIn && (
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="inline-block"
                >
                  <Link
                    to="/login"
                    className="inline-block px-12 py-5 bg-test text-primary rounded-xl font-semibold hover:bg-test/90 transition-colors shadow-lg text-xl group"
                  >
                    {t("home.cta.startTracking")}
                    <span className="ml-2 inline-block transition-transform group-hover:translate-x-1">
                      →
                    </span>
                  </Link>
                </motion.div>
              )}
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;
